import { useNavigate, useParams, useOutletContext } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import EmployeeForm, { type EmployeeFormData } from "../components/EmployeeForm";
import type { EmployeeFormOutletContext } from "../components/EmployeeEditLayout";
import { fetchEmployee } from "../api/employees";
import { useAbandonmentDetection } from "../hooks/useAbandonmentDetection";
import { usePageLoadMetric } from "../hooks/usePageLoadMetric";

/**
 * Step 間で引き回すフォーム state（新規登録・編集ともに共通）。
 * タグ設定の「設定を完了する」で初めて DB へ反映される。
 */
export type EmployeeNewFormState = EmployeeFormData;

/**
 * 従業員登録・編集ページ（基本情報タブ）
 */
const EmployeeFormPage = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const {
        setPageError,
        invalidFields,
        pendingFormState,
        setPendingFormState,
    } = useOutletContext<EmployeeFormOutletContext>();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;
    const employeeId = id ? parseInt(id, 10) : null;

    const [initialData, setInitialData] = useState<Partial<EmployeeFormData> | undefined>(
        pendingFormState ?? undefined,
    );

    // 編集モードで pendingFormState が無い場合（初回 or 従業員切替）のみ DB 取得
    const needsFetch = isEdit && employeeId !== null && pendingFormState === null;
    const [loading, setLoading] = useState(needsFetch);
    useAbandonmentDetection("employee-form", pendingFormState !== null);
    usePageLoadMetric("employee-form", !loading);

    // 編集時に従業員データを取得（pendingFormState 未設定時だけ）
    useEffect(() => {
        if (!needsFetch || !employeeId) return;
        const loadEmployeeData = async () => {
            setLoading(true);
            setPageError(null);
            try {
                const data = await fetchEmployee(employeeId);
                const mapped: EmployeeFormData = {
                    code: data.code,
                    name: data.name,
                    nickname: data.nickname || "",
                    birth_date: data.birth_date,
                    is_foreign_national: data.is_foreign_national,
                    memo: data.memo || "",
                    employment_status: data.employment_status || "在籍中",
                    store_id: data.store_id,
                    department_id: data.department_id,
                    employment_type_id: data.employment_type_id,
                    permission_level: data.permission_level || 1,
                };
                setInitialData(mapped);
                setPendingFormState(mapped);
            } catch (err) {
                console.error("Failed to fetch employee data:", err);
                setPageError(err instanceof Error ? err.message : `${t("EMPLOYEE")}データの取得に失敗しました`);
            } finally {
                setLoading(false);
            }
        };
        loadEmployeeData();
    }, [needsFetch, employeeId, setPageError, setPendingFormState, t]);

    const handleFormDataChange = useCallback((data: EmployeeFormData) => {
        setPendingFormState(data);
    }, [setPendingFormState]);

    const handleCancel = () => {
        navigate("/employee/info");
    };

    const handleNext = () => {
        const nextPath = isEdit ? `/employee/info/${id}/work-rules` : "/employee/info/new/work-rules";
        navigate(nextPath);
    };

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center">
                <span className="text-gray-500">読み込み中...</span>
            </div>
        );
    }

    return (
        <EmployeeForm
            onCancel={handleCancel}
            onNext={handleNext}
            initialData={initialData}
            onFormDataChange={handleFormDataChange}
            invalidFields={invalidFields}
        />
    );
};

export default EmployeeFormPage;
